import React from "react";
import { Helmet } from "react-helmet";
import { getCanonicalWebDomain } from "../../utils/deepLink";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
  keywords?: string[];
  noindex?: boolean;
  structuredData?: object | object[];
}

const SEO: React.FC<SEOProps> = ({
  title = "Spacia - Work, Stay & Play",
  description = "Discover flexible workspaces, unique accommodations, and curated experiences across Africa. Book coworking spaces, hotels, and experiences on one platform.",
  image = "/assets/img/spacia/web/spacia_logo_new.png",
  url,
  type = "website",
  keywords = ["spacia", "coworking", "workspace", "africa", "booking", "hotel", "accommodation", "experiences"],
  noindex = false,
  structuredData,
}) => {
  // Only render SEO on production (app.spacia.africa)
  const isProduction = typeof window !== "undefined" && getCanonicalWebDomain().endsWith(".spacia.africa");

  if (!isProduction) {
    return null;
  }

  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const fullImageUrl = image.startsWith("http")
    ? image
    : `${typeof window !== "undefined" ? window.location.origin : ""}${image}`;
  const keywordsString = keywords.join(", ");

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywordsString} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Spacia" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Social Media */}
      <meta property="og:see_also" content="https://www.instagram.com/spaciaafrica/" />

      {/* Additional SEO */}
      <meta name="author" content="Spacia" />
      <link rel="canonical" href={fullUrl} />

      {/* Structured Data */}
      {structuredData && (
        <>
          {Array.isArray(structuredData) ? (
            structuredData.map((data, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(data)}
              </script>
            ))
          ) : (
            <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
          )}
        </>
      )}
    </Helmet>
  );
};

export default SEO;
